import { ArrowRight, Check, Eye, Loader2, Plus, Search, Trash2, XCircle } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import toast from "react-hot-toast";

import { useTenantSwitcher } from "@/contexts/TenantSwitcherContext";
import { EmptyState } from "@/components/shared/EmptyState";
import { LoadingLogo } from "@/components/shared/LoadingLogo";
import { PageHeader } from "@/components/shared/PageHeader";
import { StatusBadge } from "@/components/shared/StatusBadge";
import { TableCard } from "@/components/shared/TableCard";
import { TablePagination } from "@/components/shared/TablePagination";
import {
  TableActionsMenu,
  type TableAction,
} from "@/components/shared/TableActionsMenu";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { DatePickerField } from "@/components/shared/DatePickerField";
import { statusBadgeVariant } from "@/pages/tenants/tenantStatusBadge";
import { usePaginationFromUrl } from "@/hooks/usePaginationFromUrl";
import { apiBaseUrl } from "@/services/apiClient";
import { tenantKeys } from "@/lib/queryKeys";
import {
  approveLiteralE,
  confirmDeletionRequest,
  createTenant,
  getRucInfo,
  listTenants,
  rejectLiteralE,
  syncRecibidas,
} from "@/services/platformTenantsService";
import type { PlatformTenant } from "@/services/platformTenantsService";
import type { CreateTenantPayload } from "@/services/platformTenantsService";
import { TenantMobileCard } from "@/pages/tenants/TenantMobileCard";
import { closureNoticeLabel } from "@/pages/tenants/closureNotice";

const RUC_DIGITS_LENGTH = 12;
const SEARCH_DEBOUNCE_MS = 300;

function normalizeRuc(rut: string): string {
  return rut.replace(/\D/g, "");
}

function validateRucFormat(rut: string): boolean {
  const digits = normalizeRuc(rut);
  return digits.length === RUC_DIGITS_LENGTH && /^\d+$/.test(digits);
}

const createTenantSchema = z
  .object({
    rut: z.string().min(1, "tenants.create.rutRequired"),
    legalName: z.string().optional(),
    name: z.string().min(1),
    language: z.enum(["en", "es"]),
    industry: z.string().optional(),
  })
  .refine((data) => validateRucFormat(data.rut), {
    message: "tenants.create.rutInvalidFormat",
    path: ["rut"],
  });

type CreateTenantFormValues = z.infer<typeof createTenantSchema>;

const CREATE_LANGUAGE_OPTIONS = [
  { value: "en", labelKey: "tenants.edit.form.languageEn" as const },
  { value: "es", labelKey: "tenants.edit.form.languageEs" as const },
] as const;

const INDUSTRY_OPTIONS = [
  { value: "education", label: "Education" },
  { value: "agriculture", label: "Agriculture" },
  { value: "retail", label: "Retail" },
  { value: "warehouse", label: "Warehouse" },
  { value: "library", label: "Library" },
  { value: "healthcare", label: "Healthcare" },
] as const;

const LOADING_TOAST_OPTIONS = {
  style: { background: "#e8f5f6", color: "#0e7490" } as const,
  iconTheme: { primary: "#65c1c8", secondary: "#e8f5f6" } as const,
};

const SUCCESS_TOAST_OPTIONS = {
  style: { background: "#f0fdf4", color: "#14532d" } as const,
  iconTheme: { primary: "#22c55e", secondary: "#f0fdf4" } as const,
};


export function TenantsPage(): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const pagination = usePaginationFromUrl();
  const { impersonate } = useTenantSwitcher();
  const queryClient = useQueryClient();
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [searchInput, setSearchInput] = useState(
    () => searchParams.get("search") ?? ""
  );
  const [rucInfo, setRucInfo] = useState<{
    valid: boolean;
    legalName?: string;
    fechaInicio?: string;
  } | null>(null);
  const [rucLookupLoading, setRucLookupLoading] = useState(false);

  const createForm = useForm<CreateTenantFormValues>({
    resolver: zodResolver(createTenantSchema),
    defaultValues: {
      rut: "",
      legalName: "",
      name: "",
      language: "en",
      industry: "",
    },
  });

  const filterSearch = searchParams.get("search") ?? "";
  const filterStatus = searchParams.get("status") ?? "";

  useEffect(() => {
    setSearchInput(filterSearch);
  }, [filterSearch]);

  const setFilter = useCallback(
    (key: "search" | "status", value: string) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (value) next.set(key, value);
          else next.delete(key);
          next.set("page", "1");
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const clearFilters = useCallback(() => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete("search");
        next.delete("status");
        next.set("page", "1");
        return next;
      },
      { replace: true }
    );
  }, [setSearchParams]);

  const hasActiveFilters = useMemo(
    () => Boolean(filterSearch.trim() || filterStatus),
    [filterSearch, filterStatus]
  );

  useEffect(() => {
    const timeout = window.setTimeout(() => {
      const trimmed = searchInput.trim();
      if (trimmed !== filterSearch) {
        setFilter("search", trimmed);
      }
    }, SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timeout);
  }, [searchInput, filterSearch, setFilter]);

  const listParams = useMemo(
    () => ({
      page: pagination.page,
      limit: pagination.limit,
      ...(filterSearch.trim() && { search: filterSearch.trim() }),
      ...(filterStatus && { status: filterStatus }),
    }),
    [pagination.page, pagination.limit, filterSearch, filterStatus]
  );

  const rutValue = createForm.watch("rut");
  const rutDigits = normalizeRuc(rutValue ?? "");
  const rutFormatValid = rutDigits.length === RUC_DIGITS_LENGTH && /^\d+$/.test(rutDigits);
  const lastLookedUpRucRef = useRef<string>("");

  useEffect(() => {
    if (!rutFormatValid) {
      setRucInfo(null);
      lastLookedUpRucRef.current = "";
      return;
    }
    if (lastLookedUpRucRef.current === rutDigits) return;
    lastLookedUpRucRef.current = rutDigits;
    setRucLookupLoading(true);
    getRucInfo(rutValue)
      .then((res) => {
        setRucInfo({ valid: res.valid, legalName: res.legalName, fechaInicio: res.fechaInicio });
        if (res.valid && res.legalName) {
          createForm.setValue("legalName", res.legalName);
        }
      })
      .catch(() => setRucInfo({ valid: false }))
      .finally(() => setRucLookupLoading(false));
  }, [rutFormatValid, rutDigits, rutValue, createForm]);

  const createMutation = useMutation({
    mutationFn: createTenant,
    onSuccess: (newTenant) => {
      setCreateDialogOpen(false);
      createForm.reset();
      toast.success(t("tenants.create.success"));
      queryClient.invalidateQueries({ queryKey: tenantKeys.all });
      navigate(`/tenants/${newTenant.id}/edit`);
    },
    onError: (err) => {
      const message =
        err instanceof Error ? err.message : t("tenants.create.error");
      toast.error(message);
    },
  });

  const canSubmit =
    rutFormatValid &&
    rucInfo?.valid === true &&
    !!createForm.watch("name")?.trim() &&
    !createMutation.isPending;

  const handleCreateSubmit = (values: CreateTenantFormValues): void => {
    const canonicalRut = normalizeRuc(values.rut).padStart(RUC_DIGITS_LENGTH, "0").slice(0, RUC_DIGITS_LENGTH);
    const payload: CreateTenantPayload = {
      name: values.name.trim(),
      rut: canonicalRut,
      legalName: values.legalName?.trim() || null,
      address: null,
      language: values.language,
      industry: values.industry?.trim() || null,
    };
    createMutation.mutate(payload);
  };

  // Default row click opens the tenant detail page. Entering ("impersonating")
  // the tenant is an explicit action from the detail page's header button.
  const handleRowClick = (tenantId: string, _tenantName?: string) => (): void => {
    navigate(`/tenants/${tenantId}`);
  };

  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectTenantId, setRejectTenantId] = useState<string | null>(null);
  const [rejectTenantName, setRejectTenantName] = useState<string>("");
  const [rejectNote, setRejectNote] = useState("");

  const approveMutation = useMutation({
    mutationFn: (tenantId: string) => approveLiteralE(tenantId),
    onMutate: (tenantId) => {
      toast.loading(t("tenants.page.loading.approve"), {
        id: `tenant-approve-${tenantId}`,
        ...LOADING_TOAST_OPTIONS,
      });
    },
    onSuccess: (_data, tenantId) => {
      void queryClient.invalidateQueries({ queryKey: tenantKeys.list(listParams) });
      toast.success(t("tenants.page.approveSuccess"), {
        id: `tenant-approve-${tenantId}`,
        ...SUCCESS_TOAST_OPTIONS,
      });
    },
    onError: (_err, tenantId) => {
      toast.error(t("tenants.page.error"), { id: `tenant-approve-${tenantId}` });
    },
  });
  const rejectMutation = useMutation({
    mutationFn: ({ tenantId, note }: { tenantId: string; note: string }) =>
      rejectLiteralE(tenantId, { note }),
    onMutate: ({ tenantId }) => {
      toast.loading(t("tenants.page.loading.reject"), {
        id: `tenant-reject-${tenantId}`,
        ...LOADING_TOAST_OPTIONS,
      });
    },
    onSuccess: (_data, { tenantId }) => {
      void queryClient.invalidateQueries({ queryKey: tenantKeys.list(listParams) });
      toast.success(t("tenants.page.rejectSuccess"), {
        id: `tenant-reject-${tenantId}`,
        ...SUCCESS_TOAST_OPTIONS,
      });
      setRejectDialogOpen(false);
      setRejectTenantId(null);
      setRejectTenantName("");
      setRejectNote("");
    },
    onError: (_err, { tenantId }) => {
      toast.error(t("tenants.page.error"), { id: `tenant-reject-${tenantId}` });
    },
  });

  const openRejectDialog = (tenantId: string, tenantName: string) => (): void => {
    setRejectTenantId(tenantId);
    setRejectTenantName(tenantName);
    setRejectNote("");
    setRejectDialogOpen(true);
  };
  const closeRejectDialog = (): void => {
    setRejectDialogOpen(false);
    setRejectTenantId(null);
    setRejectTenantName("");
    setRejectNote("");
  };
  const handleRejectSubmit = (): void => {
    if (!rejectTenantId || !rejectNote.trim()) return;
    rejectMutation.mutate({ tenantId: rejectTenantId, note: rejectNote.trim() });
  };

  const [confirmDeletionDialogOpen, setConfirmDeletionDialogOpen] = useState(false);
  const [confirmDeletionTenantId, setConfirmDeletionTenantId] = useState<string | null>(null);
  const [confirmDeletionTenantName, setConfirmDeletionTenantName] = useState<string>("");

  const confirmDeletionMutation = useMutation({
    mutationFn: (tenantId: string) => confirmDeletionRequest(tenantId),
    onMutate: (tenantId) => {
      toast.loading(t("tenants.page.loading.confirmDeletion"), {
        id: `tenant-confirm-deletion-${tenantId}`,
        ...LOADING_TOAST_OPTIONS,
      });
    },
    onSuccess: (_data, tenantId) => {
      void queryClient.invalidateQueries({ queryKey: tenantKeys.list(listParams) });
      toast.success(t("tenants.page.confirmDeletionSuccess"), {
        id: `tenant-confirm-deletion-${tenantId}`,
        ...SUCCESS_TOAST_OPTIONS,
      });
      setConfirmDeletionDialogOpen(false);
      setConfirmDeletionTenantId(null);
      setConfirmDeletionTenantName("");
    },
    onError: (_err, tenantId) => {
      toast.error(t("tenants.page.error"), { id: `tenant-confirm-deletion-${tenantId}` });
    },
  });

  const openConfirmDeletionDialog = (tenantId: string, tenantName: string) => (): void => {
    setConfirmDeletionTenantId(tenantId);
    setConfirmDeletionTenantName(tenantName);
    setConfirmDeletionDialogOpen(true);
  };

  const getDefaultSyncDateRange = useCallback((): { dateFrom: string; dateTo: string } => {
    const now = new Date();
    const y = now.getFullYear();
    const m = String(now.getMonth() + 1).padStart(2, "0");
    const d = String(now.getDate()).padStart(2, "0");
    return { dateFrom: `${y}-${m}-01`, dateTo: `${y}-${m}-${d}` };
  }, []);
  const [syncRecibidasDialogOpen, setSyncRecibidasDialogOpen] = useState(false);
  const [syncRecibidasTenantId, setSyncRecibidasTenantId] = useState<string | null>(null);
  const [syncRecibidasTenantName, setSyncRecibidasTenantName] = useState<string>("");
  const [syncDateFrom, setSyncDateFrom] = useState("");
  const [syncDateTo, setSyncDateTo] = useState("");

  const syncRecibidasMutation = useMutation({
    mutationFn: ({ tenantId, dateFrom, dateTo }: { tenantId: string; dateFrom: string; dateTo: string }) =>
      syncRecibidas(tenantId, { dateFrom, dateTo }),
    onMutate: (vars) => {
      toast.loading(t("tenants.page.recibidasSync.loading"), {
        id: `tenant-sync-recibidas-${vars.tenantId}`,
        ...LOADING_TOAST_OPTIONS,
      });
    },
    onSuccess: (data, { tenantId }) => {
      let message: string;
      if (data.processed === 0) {
        message = t("tenants.page.recibidasSync.successEmpty");
      } else if (data.inserted === 0) {
        message = t("tenants.page.recibidasSync.successNoNew", { count: data.processed });
      } else if (data.updated === 0) {
        message = t("tenants.page.recibidasSync.successNewOnly", { count: data.inserted });
      } else {
        message = t("tenants.page.recibidasSync.successNew", {
          inserted: data.inserted,
          updated: data.updated,
        });
      }
      toast.success(message, {
        id: `tenant-sync-recibidas-${tenantId}`,
        ...SUCCESS_TOAST_OPTIONS,
      });
      setSyncRecibidasDialogOpen(false);
      setSyncRecibidasTenantId(null);
      setSyncRecibidasTenantName("");
    },
    onError: (_err, { tenantId }) => {
      toast.error(t("tenants.page.recibidasSync.error"), {
        id: `tenant-sync-recibidas-${tenantId}`,
      });
    },
  });

  const openSyncRecibidasDialog = (tenantId: string, tenantName: string) => (): void => {
    const { dateFrom, dateTo } = getDefaultSyncDateRange();
    setSyncRecibidasTenantId(tenantId);
    setSyncRecibidasTenantName(tenantName);
    setSyncDateFrom(dateFrom);
    setSyncDateTo(dateTo);
    setSyncRecibidasDialogOpen(true);
  };
  const closeSyncRecibidasDialog = (): void => {
    setSyncRecibidasDialogOpen(false);
    setSyncRecibidasTenantId(null);
    setSyncRecibidasTenantName("");
  };
  const handleSyncRecibidasSubmit = (): void => {
    if (!syncRecibidasTenantId || !syncDateFrom.trim() || !syncDateTo.trim()) return;
    const from = new Date(syncDateFrom.slice(0, 10));
    const to = new Date(syncDateTo.slice(0, 10));
    if (from.getTime() > to.getTime()) {
      toast.error(t("tenants.page.recibidasSync.dateFromBeforeDateTo"));
      return;
    }
    syncRecibidasMutation.mutate({
      tenantId: syncRecibidasTenantId,
      dateFrom: syncDateFrom.slice(0, 10),
      dateTo: syncDateTo.slice(0, 10),
    });
  };

  const closeConfirmDeletionDialog = (): void => {
    setConfirmDeletionDialogOpen(false);
    setConfirmDeletionTenantId(null);
    setConfirmDeletionTenantName("");
  };
  const handleConfirmDeletionSubmit = (): void => {
    if (!confirmDeletionTenantId) return;
    confirmDeletionMutation.mutate(confirmDeletionTenantId);
  };

  const {
    data: listData,
    isLoading: loading,
    isFetching,
    isError,
    refetch,
  } = useQuery({
    queryKey: tenantKeys.list(listParams),
    queryFn: () => listTenants(listParams),
  });

  const tenants: PlatformTenant[] = listData?.data ?? [];
  useEffect(() => {
    if (listData != null) {
      pagination.setTotal(listData.total);
    } else if (isError) {
      pagination.setTotal(0);
    }
  }, [listData, isError, pagination]);

  useEffect(() => {
    if (isError) {
      toast.error(t("tenants.page.error"));
    }
  }, [isError, t]);

  const handleImpersonate = async (
    tenantId: string,
    tenantName?: string
  ): Promise<void> => {
    await impersonate(tenantId, tenantName);
    navigate("/dashboard", { replace: true });
  };

  return (
    <div className="space-y-4 fx-content">
      <PageHeader
        eyebrow={t("tenants.eyebrow", "Plataforma")}
        title={t("tenants.page.title")}
        actions={
          <Button onClick={() => setCreateDialogOpen(true)}>
            <Plus className="mr-2 h-4 w-4" />
            {t("tenants.page.createBusiness")}
          </Button>
        }
      />
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative w-[220px]">
          <Search
            className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
            aria-hidden
          />
          <Input
            type="search"
            placeholder={t("tenants.page.filters.searchPlaceholder")}
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="w-full pl-9"
            aria-label={t("tenants.page.filters.searchPlaceholder")}
          />
        </div>
        <Select
          value={filterStatus || "all"}
          onValueChange={(v) => setFilter("status", v === "all" ? "" : v)}
        >
          <SelectTrigger className="w-[180px]">
            <SelectValue
              placeholder={t("tenants.page.filters.statusAll")}
            />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">
              {t("tenants.page.filters.statusAll")}
            </SelectItem>
            <SelectItem value="active">
              {t("tenants.page.statusActive")}
            </SelectItem>
            <SelectItem value="onboarding">
              {t("tenants.page.statusOnboarding")}
            </SelectItem>
            <SelectItem value="pending_approval">
              {t("tenants.page.statusPendingApproval")}
            </SelectItem>
            <SelectItem value="literal_e_rejected">
              {t("tenants.page.statusLiteralERejected", "Literal E rechazado")}
            </SelectItem>
            <SelectItem value="pending_deletion">
              {t("tenants.page.statusPendingDeletion")}
            </SelectItem>
          </SelectContent>
        </Select>
        {hasActiveFilters && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={clearFilters}
          >
            {t("tenants.page.filters.clearFilters")}
          </Button>
        )}
      </div>
      <Dialog
        open={createDialogOpen}
        onOpenChange={(open) => {
          setCreateDialogOpen(open);
          setRucInfo(null);
          lastLookedUpRucRef.current = "";
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("tenants.create.title")}</DialogTitle>
            <DialogDescription>
              {t("tenants.create.description")}
            </DialogDescription>
          </DialogHeader>
          <form
            id="create-tenant-form"
            className="space-y-4"
            onSubmit={createForm.handleSubmit(handleCreateSubmit)}
          >
            <div className="grid gap-2">
              <Label htmlFor="create-tenant-rut">
                {t("tenants.create.rut")} *
              </Label>
              <Input
                id="create-tenant-rut"
                {...createForm.register("rut")}
                placeholder={t("tenants.create.rutPlaceholder")}
                disabled={createMutation.isPending}
              />
              {rucLookupLoading ? (
                <p className="text-sm text-muted-foreground">
                  {t("tenants.create.rutLookingUp")}
                </p>
              ) : null}
              {createForm.formState.errors.rut ? (
                <p className="text-sm text-destructive">
                  {t(createForm.formState.errors.rut.message as string)}
                </p>
              ) : rutFormatValid && rucInfo && !rucInfo.valid ? (
                <p className="text-sm text-destructive">
                  {t("tenants.create.rutNotFound")}
                </p>
              ) : null}
            </div>
            <div className="grid gap-2">
              <Label htmlFor="create-tenant-legal-name">
                {t("tenants.create.legalName")}
              </Label>
              <Input
                id="create-tenant-legal-name"
                {...createForm.register("legalName")}
                placeholder={t("tenants.edit.form.legalNamePlaceholder")}
                disabled={createMutation.isPending}
              />
              {rucInfo?.fechaInicio ? (
                <p className="text-xs text-muted-foreground">
                  {t("tenants.create.emisorDesde")}: {rucInfo.fechaInicio}
                </p>
              ) : null}
            </div>
            <div className="grid gap-2">
              <Label htmlFor="create-tenant-name">
                {t("tenants.create.name")} *
              </Label>
              <Input
                id="create-tenant-name"
                {...createForm.register("name")}
                placeholder={t("tenants.edit.form.namePlaceholder")}
                disabled={createMutation.isPending}
              />
              {createForm.formState.errors.name ? (
                <p className="text-sm text-destructive">
                  {t("tenants.edit.form.nameRequired")}
                </p>
              ) : null}
            </div>
            <div className="grid gap-2">
              <Label htmlFor="create-tenant-language">
                {t("tenants.create.language")}
              </Label>
              <Controller
                name="language"
                control={createForm.control}
                render={({ field }) => (
                  <Select
                    value={field.value}
                    onValueChange={(value) =>
                      field.onChange(value as "en" | "es")
                    }
                    disabled={createMutation.isPending}
                  >
                    <SelectTrigger id="create-tenant-language">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CREATE_LANGUAGE_OPTIONS.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {t(option.labelKey)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="create-tenant-industry">
                {t("tenants.create.industry")}
              </Label>
              <Controller
                name="industry"
                control={createForm.control}
                render={({ field }) => (
                  <Select
                    value={field.value || undefined}
                    onValueChange={(value) => field.onChange(value || "")}
                    disabled={createMutation.isPending}
                  >
                    <SelectTrigger id="create-tenant-industry">
                      <SelectValue placeholder={t("tenants.create.industryPlaceholder")} />
                    </SelectTrigger>
                    <SelectContent>
                      {INDUSTRY_OPTIONS.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {t(`tenants.create.industryOptions.${option.value}`, option.label)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setCreateDialogOpen(false)}
                disabled={createMutation.isPending}
              >
                {t("tenants.create.cancel")}
              </Button>
              <Button
                type="submit"
                form="create-tenant-form"
                disabled={!canSubmit}
              >
                {createMutation.isPending ? t("tenants.create.submitting") : t("tenants.create.submit")}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
      <Dialog open={rejectDialogOpen} onOpenChange={(open) => !open && closeRejectDialog()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("tenants.page.rejectDialog.title")}</DialogTitle>
            <DialogDescription>
              {t("tenants.page.rejectDialog.description")}
              {rejectTenantName ? ` (${rejectTenantName})` : ""}
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-2 py-2">
            <Label htmlFor="reject-note">{t("tenants.page.rejectDialog.noteLabel")} *</Label>
            <textarea
              id="reject-note"
              className="min-h-[120px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
              placeholder={t("tenants.page.rejectDialog.notePlaceholder")}
              value={rejectNote}
              onChange={(e) => setRejectNote(e.target.value)}
              disabled={rejectMutation.isPending}
              maxLength={2000}
            />
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={closeRejectDialog}
              disabled={rejectMutation.isPending}
            >
              {t("tenants.page.rejectDialog.cancel")}
            </Button>
            <Button
              type="button"
              onClick={handleRejectSubmit}
              disabled={!rejectNote.trim() || rejectMutation.isPending}
            >
              {rejectMutation.isPending ? t("tenants.page.rejectDialog.submitting") : t("tenants.page.rejectDialog.submit")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={confirmDeletionDialogOpen} onOpenChange={(open) => !open && closeConfirmDeletionDialog()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("tenants.page.confirmDeletionDialog.title")}</DialogTitle>
            <DialogDescription>
              {t("tenants.page.confirmDeletionDialog.description", {
                name: confirmDeletionTenantName || "",
              })}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={closeConfirmDeletionDialog}
              disabled={confirmDeletionMutation.isPending}
            >
              {t("tenants.page.confirmDeletionDialog.cancel")}
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={handleConfirmDeletionSubmit}
              disabled={confirmDeletionMutation.isPending}
            >
              {confirmDeletionMutation.isPending
                ? t("tenants.page.rejectDialog.submitting")
                : t("tenants.page.confirmDeletionDialog.confirm")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={syncRecibidasDialogOpen} onOpenChange={(open) => !open && closeSyncRecibidasDialog()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("tenants.page.recibidasSync.title")}</DialogTitle>
            <DialogDescription>
              {t("tenants.page.recibidasSync.description", {
                name: syncRecibidasTenantName || "",
              })}
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4 sm:grid-cols-2">
            <DatePickerField
              id="sync-dateFrom"
              label={t("tenants.page.recibidasSync.dateFrom")}
              value={syncDateFrom}
              onChange={setSyncDateFrom}
              disabled={syncRecibidasMutation.isPending}
            />
            <DatePickerField
              id="sync-dateTo"
              label={t("tenants.page.recibidasSync.dateTo")}
              value={syncDateTo}
              onChange={setSyncDateTo}
              disabled={syncRecibidasMutation.isPending}
            />
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={closeSyncRecibidasDialog}
              disabled={syncRecibidasMutation.isPending}
            >
              {t("tenants.page.recibidasSync.cancel")}
            </Button>
            <Button
              type="button"
              onClick={handleSyncRecibidasSubmit}
              disabled={syncRecibidasMutation.isPending}
            >
              {syncRecibidasMutation.isPending
                ? t("tenants.page.recibidasSync.submitting")
                : t("tenants.page.recibidasSync.submit")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <TableCard>
        {loading ? (
          <div className="flex min-h-[40vh] items-center justify-center py-12">
            <LoadingLogo size="lg" />
          </div>
        ) : tenants.length === 0 ? (
          <EmptyState title={t("tenants.page.empty")} />
        ) : (
          <>
            <div className="space-y-3 py-2 md:hidden">
              {tenants.map((tenant) => {
                const logoUrlMob =
                  tenant.profileImage != null && tenant.profileImage !== ""
                    ? tenant.profileImage.startsWith("http")
                      ? tenant.profileImage
                      : `${apiBaseUrl}${tenant.profileImage}`
                    : null;
                const displayNameMob =
                  tenant.legalName?.trim() ?? tenant.name;
                const actionsMob: TableAction[] = [
                  {
                    label: t("tenants.page.actions.viewDetails"),
                    icon: Eye,
                    onClick: () => navigate(`/tenants/${tenant.id}`),
                  },
                  ...(tenant.status === "pending_approval"
                    ? [
                        {
                          label: t("tenants.page.actions.approve"),
                          icon: Check,
                          onClick: () => approveMutation.mutate(tenant.id),
                        },
                        {
                          label: t("tenants.page.actions.reject"),
                          icon: XCircle,
                          onClick: openRejectDialog(tenant.id, displayNameMob),
                        },
                      ]
                    : []),
                  ...(tenant.status === "pending_deletion"
                    ? [
                        {
                          label: t("tenants.page.actions.confirmDeletion"),
                          icon: Trash2,
                          onClick: openConfirmDeletionDialog(tenant.id, displayNameMob),
                        },
                      ]
                    : []),
                  {
                    label: t("tenants.page.actions.impersonate"),
                    icon: ArrowRight,
                    onClick: () =>
                      void handleImpersonate(tenant.id, displayNameMob),
                  },
                ];
                return (
                  <TenantMobileCard
                    key={tenant.id}
                    tenant={tenant}
                    logoUrl={logoUrlMob}
                    displayName={displayNameMob}
                    actions={actionsMob}
                    onCardClick={handleRowClick(tenant.id, displayNameMob)}
                  />
                );
              })}
            </div>
            <div className="hidden md:block">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[56px]" />
                  <TableHead>{t("tenants.page.legalName")}</TableHead>
                  <TableHead>{t("tenants.page.rut")}</TableHead>
                  <TableHead>{t("tenants.page.dgiEnvColumn")}</TableHead>
                  <TableHead>{t("tenants.page.status")}</TableHead>
                  <TableHead>{t("tenants.page.accountantColumn", "Contador")}</TableHead>
                  <TableHead className="max-w-[200px]">{t("tenants.page.notesColumn")}</TableHead>
                  <TableHead className="w-[70px]" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {tenants.map((tenant) => {
                  const logoUrl =
                    tenant.profileImage != null && tenant.profileImage !== ""
                      ? tenant.profileImage.startsWith("http")
                        ? tenant.profileImage
                        : `${apiBaseUrl}${tenant.profileImage}`
                      : null;
                  const displayName =
                    tenant.legalName?.trim() ?? tenant.name;
                  const actions: TableAction[] = [
                    {
                      label: t("tenants.page.actions.viewDetails"),
                      icon: Eye,
                      onClick: () => navigate(`/tenants/${tenant.id}`),
                    },
                    ...(tenant.status === "pending_approval"
                      ? [
                          {
                            label: t("tenants.page.actions.approve"),
                            icon: Check,
                            onClick: () =>
                              approveMutation.mutate(tenant.id),
                          },
                          {
                            label: t("tenants.page.actions.reject"),
                            icon: XCircle,
                            onClick: openRejectDialog(tenant.id, displayName),
                          },
                        ]
                      : []),
                    ...(tenant.status === "pending_deletion"
                      ? [
                          {
                            label: t("tenants.page.actions.confirmDeletion"),
                            icon: Trash2,
                            onClick: openConfirmDeletionDialog(tenant.id, displayName),
                          },
                        ]
                      : []),
                    {
                      label: t("tenants.page.actions.impersonate"),
                      icon: ArrowRight,
                      onClick: () =>
                        void handleImpersonate(tenant.id, displayName),
                    },
                  ];
                  return (
                    <TableRow
                      key={tenant.id}
                      onRowClick={handleRowClick(tenant.id, displayName)}
                    >
                      <TableCell>
                        <Avatar className="h-8 w-8">
                          {logoUrl ? (
                            <AvatarImage src={logoUrl} alt="" />
                          ) : null}
                          <AvatarFallback className="text-xs">
                            {displayName.charAt(0)}
                          </AvatarFallback>
                        </Avatar>
                      </TableCell>
                      <TableCell>{displayName}</TableCell>
                      <TableCell>{tenant.rut}</TableCell>
                      <TableCell>
                        {tenant.dgiEnv?.toLowerCase() === "prod" ? (
                          <StatusBadge variant="success">
                            {t("tenants.edit.dgiEnvProd")}
                          </StatusBadge>
                        ) : tenant.dgiEnv?.toLowerCase() === "test" ? (
                          <StatusBadge variant="info">
                            {t("tenants.edit.dgiEnvTest")}
                          </StatusBadge>
                        ) : (
                          <Badge variant="secondary">
                            {t("tenants.page.dgiEnvSystem")}
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        {tenant.status ? (
                          <StatusBadge
                            variant={statusBadgeVariant(tenant.status)}
                          >
                            {tenant.status === "active"
                              ? t("tenants.page.statusActive")
                              : tenant.status === "onboarding"
                                ? t("tenants.page.statusOnboarding")
                                : tenant.status === "literal_e_rejected"
                                  ? t(
                                      "tenants.page.statusLiteralERejected",
                                      "Literal E rechazado",
                                    )
                                  : tenant.status === "pending_approval"
                                  ? t("tenants.page.statusPendingApproval")
                                  : t("tenants.page.statusPendingDeletion")}
                          </StatusBadge>
                        ) : (
                          "—"
                        )}
                        {closureNoticeLabel(tenant.closureNoticeSentAt, t)}
                      </TableCell>
                      <TableCell>
                        {tenant.accountantName?.trim() ? (
                          tenant.accountantName
                        ) : (
                          <span className="text-muted-foreground">
                            {t("tenants.page.accountantAuto", "Auto")}
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="max-w-[200px] truncate" title={tenant.literalERejectionNote ?? undefined}>
                        {tenant.literalERejectionNote?.trim() ? tenant.literalERejectionNote : "—"}
                      </TableCell>
                      <TableCell>
                        <TableActionsMenu actions={actions} />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            </div>
            <TablePagination
              pagination={pagination}
              rowsPerPageLabel={t("tenants.page.rowsPerPage")}
              pageOfLabel={t("tenants.page.pageOf", {
                from:
                  (pagination.page - 1) * pagination.limit + 1,
                to: Math.min(
                  pagination.page * pagination.limit,
                  pagination.total
                ),
                total: pagination.total,
              })}
              onRefresh={() => void refetch()}
              isRefreshing={isFetching}
            />
          </>
        )}
      </TableCard>
    </div>
  );
}
